import { useEffect } from 'react'
import type { PageMeta } from '../domain/meta'

/**
 * Writes one page's title and meta tags into the document head.
 *
 * A component that renders nothing, rather than a hook, so a page declares its metadata in its JSX
 * beside everything else it renders. There is no `react-helmet` here and there does not need to be:
 * this app has four pages and one of them matters.
 *
 * **What this can and cannot do.** Google renders the page and reads what this writes. The social
 * crawlers do not run JavaScript, so on a shared link they read `index.html` as served and see the
 * home page's `og:` tags whatever the plate. The `og:` values below are written anyway: they cost
 * nothing, they are correct for anything that does render, and they are the exact strings a
 * server-rendered shell would need if that is ever built. See `domain/meta.ts`.
 *
 * Tags are updated in place and never removed. Every page sets all of them, so there is nothing to
 * clean up between navigations, and removing them on unmount would leave a window where a crawler
 * that *did* render could read a head with no description in it.
 */
export function DocumentMeta({ title, description, image }: PageMeta) {
  useEffect(() => {
    document.title = title

    setMeta('name', 'description', description)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', window.location.href)
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)

    // A card with a picture is worth several times one without, so the card type follows the image
    // rather than being fixed: `summary_large_image` with nothing to show renders as a broken box.
    setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary')
    setMeta('property', 'og:image', image)
    setMeta('name', 'twitter:image', image)

    setCanonical(window.location.href)
  }, [title, description, image])

  return null
}

/**
 * Sets a meta tag, creating it if the static head does not already carry one.
 *
 * An absent value empties the tag rather than removing it: a page with no image following one that
 * had an image must not inherit it, and leaving the element in place keeps this idempotent.
 */
function setMeta(keyName: 'name' | 'property', key: string, value: string | undefined) {
  const selector = `meta[${keyName}="${key}"]`
  let tag = document.head.querySelector<HTMLMetaElement>(selector)

  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(keyName, key)
    document.head.appendChild(tag)
  }

  tag.setAttribute('content', value ?? '')
}

/**
 * The canonical URL.
 *
 * It matters more here than on most sites: `ReportPage` redirects a plate written any other way to
 * its compact form, and `responseOverrides` turns every unmatched path into the app, so one report
 * is reachable by more than one URL and only one of them should be indexed.
 */
function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')

  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }

  link.href = href
}
